import campaignV2CreatorsFetchers from '@/api/fetchers/campaignV2Creators';
import type {
  CampaignCreatorApiResponse,
  CampaignCreatorRow,
  CreatorStatus,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import mapApiResponseToCreatorRow from '@/views/campaigns/creators-tab/utils/mapApiResponseToCreatorRow';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page';
import useInfiniteDataQuery from '@/hooks/use-infinite-data-query';

const PER_PAGE = 50;

export const CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY = 'campaignV2Creators';

interface UseCampaignV2CreatorsQueryParams {
  brandId?: string;
  campaignId?: string;
  status: CreatorStatus;
  search?: string;
  perPage?: number;
  enabled?: boolean;
  shortlisted?: boolean;
  hasUnreadMessages?: boolean;
}

function useCampaignV2CreatorsQuery({
  brandId,
  campaignId,
  status,
  search = '',
  perPage = PER_PAGE,
  enabled = true,
  shortlisted,
  hasUnreadMessages,
}: UseCampaignV2CreatorsQueryParams) {
  const response = useInfiniteDataQuery({
    queryKey: [
      CAMPAIGN_V2_CREATORS_BASE_QUERY_KEY,
      {
        campaignId,
        status,
        search,
        perPage,
        brandId,
        shortlisted,
        hasUnreadMessages,
      },
    ],
    queryFn: (context: {queryKey: unknown[]; pageParam?: number}) =>
      campaignV2CreatorsFetchers.list({
        ...context,
        meta: {
          campaignId,
          status,
          search,
          page: context.pageParam ?? 1,
          perPage,
          brandId,
          shortlisted,
          hasUnreadMessages,
        },
      }),
    enabled: !!campaignId && enabled,
    keepPreviousData: true,
    getNextPageParam: (lastPage: {
      meta?: {pagination?: {currentPage: number; totalPages: number}};
    }) => {
      const pagination = lastPage?.meta?.pagination;
      if (!pagination) return undefined;
      if (pagination.currentPage >= pagination.totalPages) return undefined;
      return pagination.currentPage + 1;
    },
  });

  const {data} = response;
  const pages = (data as {pages?: {data: CampaignCreatorApiResponse[]}[]})
    ?.pages;
  const creators: CampaignCreatorRow[] = pages
    ? pages.flatMap((page) =>
        (page?.data ?? []).map((row) => mapApiResponseToCreatorRow(row))
      )
    : [];

  const fetchNextPage = useGuardedFetchNextPage(
    response.fetchNextPage,
    response.isFetching
  );

  return {
    creators,
    isLoading: response.isLoading,
    isFetching: response.isFetching,
    hasNextPage: response.hasNextPage ?? false,
    fetchNextPage,
  };
}

export default useCampaignV2CreatorsQuery;
